import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ImpersonatedUser {
  id: string;
  email: string;
  name: string;
  type: 'user' | 'member';
  companyName?: string;
  companyId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private readonly STORAGE_KEY = 'meetzy_impersonation';
  
  private impersonatedUserSubject = new BehaviorSubject<ImpersonatedUser | null>(null);
  public impersonatedUser$: Observable<ImpersonatedUser | null> = this.impersonatedUserSubject.asObservable();

  constructor() {
    // Load impersonation state from sessionStorage on init
    this.loadFromStorage();
    console.log('🎭 ImpersonationService initialized, isImpersonating:', this.isImpersonating());
  }

  /**
   * Check if currently impersonating someone
   */
  isImpersonating(): boolean {
    return this.impersonatedUserSubject.value !== null;
  }

  /**
   * Get the currently impersonated user
   */
  getImpersonatedUser(): ImpersonatedUser | null {
    return this.impersonatedUserSubject.value;
  }

  /**
   * Get the impersonated user ID (for API calls)
   */
  getImpersonatedUserId(): string | null {
    const user = this.impersonatedUserSubject.value;
    return user ? user.id : null;
  }

  /**
   * Get the impersonated user type (user or member)
   */
  getImpersonatedUserType(): 'user' | 'member' | null {
    const user = this.impersonatedUserSubject.value;
    return user ? user.type : null;
  }

  /**
   * Start impersonating a user
   */
  startImpersonation(user: ImpersonatedUser): void {
    console.log('🎭 ImpersonationService: Starting impersonation of', user.email);
    this.impersonatedUserSubject.next(user);
    this.saveToStorage(user);
  }

  /**
   * Stop impersonating and return to admin view
   */
  stopImpersonation(): void {
    console.log('🎭 ImpersonationService: Stopping impersonation');
    this.impersonatedUserSubject.next(null);
    this.clearStorage();
  }

  /**
   * Save impersonation state to sessionStorage
   */
  private saveToStorage(user: ImpersonatedUser): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('🎭 ImpersonationService: Error saving to sessionStorage:', error);
    }
  }

  /**
   * Load impersonation state from sessionStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const user = JSON.parse(stored) as ImpersonatedUser;
        console.log('🎭 ImpersonationService: Restored impersonation of', user.email);
        this.impersonatedUserSubject.next(user);
      }
    } catch (error) {
      console.error('🎭 ImpersonationService: Error loading from sessionStorage:', error);
      this.clearStorage();
    }
  }

  /**
   * Clear impersonation from sessionStorage
   */
  private clearStorage(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('🎭 ImpersonationService: Error clearing sessionStorage:', error);
    }
  }
}

